import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Search, Plus, Minus, Trash2, CreditCard, Banknote, QrCode, Check, X, Printer, Tag } from "lucide-react";
import { Receipt, printOrder, printReceipt } from "@/components/Receipt";
import { buildOfflineQueueItem, classifySyncFailure, getOrCreateCartIdempotencyKey } from "@/lib/offlineQueue";

const rupiah = (v) => "Rp" + (Math.round(v || 0)).toLocaleString("id-ID");

export default function POS() {
  const { user } = useAuth();
  const [outlets, setOutlets] = useState([]);
  const [outletId, setOutletId] = useState("");
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [availability, setAvailability] = useState({});
  const [availabilityReady, setAvailabilityReady] = useState(false);
  const [q, setQ] = useState("");
  const [cart, setCart] = useState([]);
  const [payOpen, setPayOpen] = useState(false);
  const [method, setMethod] = useState("cash");
  const [cash, setCash] = useState("");
  const [qrisPayment, setQrisPayment] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [receiptTx, setReceiptTx] = useState(null);
  const [promos, setPromos] = useState([]);
  const [selectedPromo, setSelectedPromo] = useState(null); // {id,type,value,name} OR {manual:true,value,name:"Manual"}
  const [activeShift, setActiveShift] = useState(null);
  const [manualDisc, setManualDisc] = useState("");
  const cartIdempotencyKey = useRef(null);

  useEffect(() => {
    if (!outletId) {
      setCustomers([]);
      setCustomerId("");
      return;
    }

    setCustomerId("");
    api.get(`/customers?active=true&outlet_id=${encodeURIComponent(outletId)}`)
      .then(({ data }) => setCustomers(data || []))
      .catch(() => setCustomers([]));
  }, [outletId]);

  useEffect(() => {
    api.get("/outlets").then(({ data }) => {
      setOutlets(data);
      const first = user.outlet_id || data[0]?.id;
      if (first) setOutletId(first);
    });
  }, [user]);

  useEffect(() => {
    if (!outletId) return;

    setProducts([]);
    setAvailability({});
    setAvailabilityReady(false);

    Promise.all([
      api.get(`/products?outlet_id=${outletId}`),
      api.get(`/pos/availability?outlet_id=${outletId}`),
    ]).then(([productsRes, availabilityRes]) => {
      const nextAvailability = {};
      for (const row of availabilityRes.data || []) {
        nextAvailability[row.product_id] = row;
      }

      setProducts(productsRes.data);
      setAvailability(nextAvailability);
      setAvailabilityReady(true);
    }).catch(() => {
      setProducts([]);
      setAvailability({});
      setAvailabilityReady(false);
      toast.error("Stok outlet belum dapat diverifikasi. POS ditahan sementara.");
    });

    api.get(`/promos?outlet_id=${outletId}&active=true`).then(({ data }) => setPromos(data));
    if (user?.role === "kasir") {
      api.get(`/shifts/active?outlet_id=${outletId}`).then(({ data }) => setActiveShift(data || null)).catch(() => setActiveShift(null));
    } else {
      setActiveShift({ id: "manager-bypass" });
    }
  }, [outletId, user?.role]);

  // Offline queue drainer
  useEffect(() => {
    async function drain() {
      const queue = JSON.parse(localStorage.getItem("mazboy_offline_queue") || "[]");
      if (!queue.length) return;
      const remaining = [];
      const failed = JSON.parse(localStorage.getItem("mazboy_offline_failed") || "[]");
      for (const item of queue) {
        try {
          await api.post("/transactions", item.payload, { timeout: 15000 });
        } catch (e) {
          const status = e?.response?.status;
          if (classifySyncFailure(e) === "permanent") {
            failed.push({ ...item, failed_at: Date.now(), status, reason: e?.response?.data?.detail || "Permanent client error" });
          } else {
            remaining.push(item);
          }
        }
      }
      localStorage.setItem("mazboy_offline_queue", JSON.stringify(remaining));
      localStorage.setItem("mazboy_offline_failed", JSON.stringify(failed));
      if (queue.length > remaining.length) {
        toast.success(`${queue.length - remaining.length} transaksi offline tersinkron ✓`);
      }
    }
    drain();
    window.addEventListener("online", drain);
    return () => window.removeEventListener("online", drain);
  }, []);

  const filtered = useMemo(() => {
    const query = q.toLowerCase().trim();
    return products.filter(p => p.active !== false && (!query || p.name.toLowerCase().includes(query) || p.sku?.toLowerCase().includes(query)));
  }, [products, q]);

  const subtotal = cart.reduce((s, c) => s + c.price * c.qty, 0);
  const discount = (() => {
    if (!selectedPromo) return 0;
    if (selectedPromo.manual) return Math.min(Number(selectedPromo.value) || 0, subtotal);
    if (selectedPromo.min_purchase && subtotal < selectedPromo.min_purchase) return 0;
    if (selectedPromo.type === "percent") return Math.round(subtotal * (Number(selectedPromo.value) / 100));
    return Math.min(Number(selectedPromo.value) || 0, subtotal);
  })();
  const total = Math.max(0, subtotal - discount);

  function getEffectiveStock(productId) {
    if (!availabilityReady) return 0;

    const row = availability[productId];
    if (!row) return 0;

    return row.effective_stock;
  }

  function addToCart(p) {
    const effectiveStock = getEffectiveStock(p.id);

    if (effectiveStock !== null && effectiveStock !== undefined && effectiveStock <= 0) {
      toast.error("Stok produk/bahan baku di outlet tidak mencukupi");
      return;
    }

    if (!cartIdempotencyKey.current) cartIdempotencyKey.current = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`);

    setCart(c => {
      const ex = c.find(x => x.product_id === p.id);

      if (ex) {
        if (effectiveStock !== null && effectiveStock !== undefined && ex.qty >= effectiveStock) {
          toast.error(`Stok tersedia hanya ${effectiveStock}`);
          return c;
        }
        return c.map(x => x.product_id === p.id ? { ...x, qty: x.qty + 1 } : x);
      }

      return [...c, { product_id: p.id, name: p.name, price: p.price, qty: 1 }];
    });
  }

  function updateQty(id, delta) {
    setCart(c => c.map(x => {
      if (x.product_id !== id) return x;

      const effectiveStock = getEffectiveStock(id);
      const nextQty = x.qty + delta;

      if (
        delta > 0 &&
        effectiveStock !== null &&
        effectiveStock !== undefined &&
        nextQty > effectiveStock
      ) {
        toast.error(`Stok tersedia hanya ${effectiveStock}`);
        return x;
      }

      return { ...x, qty: Math.max(0, nextQty) };
    }).filter(x => x.qty > 0));
  }

  function removeItem(id) { setCart(c => c.filter(x => x.product_id !== id)); }
  function clearCart() { setCart([]); cartIdempotencyKey.current = null; setQrisPayment(null); setSelectedPromo(null); setManualDisc(""); setCustomerId(""); }

  function applyManual() {
    const v = Number(manualDisc);
    if (!v || v <= 0) return toast.error("Nominal diskon harus > 0");
    setSelectedPromo({ manual: true, value: v, type: "nominal", name: "Diskon Manual" });
  }

  async function submitPayment() {
    if (!cart.length) { toast.error("Cart kosong"); return; }
    if (method === "cash" && (!cash || Number(cash) < total)) { toast.error("Uang tunai kurang"); return; }
    setProcessing(true);
    const idempotency_key = getOrCreateCartIdempotencyKey(cartIdempotencyKey.current, () => (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`));
    cartIdempotencyKey.current = idempotency_key;
    const payload = {
      outlet_id: outletId,
      customer_id: customerId || null,
      items: cart.map(c => ({ ...c, subtotal: c.price * c.qty, notes: "" })),
      subtotal, discount, total,
      payment_method: method,
      cash_received: method === "cash" ? Number(cash) : 0,
      idempotency_key,
      client_created_at: new Date().toISOString(),
      promo_id: selectedPromo?.manual ? null : (selectedPromo?.id || null),
      promo_name: selectedPromo?.name || null,
      promo_type: selectedPromo?.type || null,
      promo_value: selectedPromo?.value || 0,
    };
    try {
      const { data: tx } = await api.post("/transactions", payload, { timeout: 15000 });
      if (method === "qris") {
        const { data: pay } = await api.post("/payments/qris/create", { transaction_id: tx.id, amount: total });
        setQrisPayment({ ...pay, transaction_id: tx.id, tx });
      } else {
        toast.success(`Transaksi ${tx.trx_no} sukses · Kembalian ${rupiah(tx.change)}`);
        setPayOpen(false);
        clearCart();
        setCash("");
        setReceiptTx(tx);
        if (currentOutlet?.printer_config?.auto_print_receipt) {
          setTimeout(() => printReceipt(tx, currentOutlet), 300);
        }
      }
    } catch (e) {
      // Offline queue
      if (!navigator.onLine || e?.code === "ERR_NETWORK") {
        const queue = JSON.parse(localStorage.getItem("mazboy_offline_queue") || "[]");
        queue.push(buildOfflineQueueItem(payload, { tenantId: user?.tenant_id, outletId, userId: user?.id }));
        localStorage.setItem("mazboy_offline_queue", JSON.stringify(queue));
        toast.success(`Transaksi disimpan offline (queue: ${queue.length}). Akan disinkronisasi saat online.`);
        setPayOpen(false); clearCart(); setCash("");
      } else {
        toast.error(e?.response?.data?.detail || "Gagal proses");
      }
    } finally {
      setProcessing(false);
    }
  }

  async function confirmStaticQrisPaid() {
    if (!qrisPayment) return;
    setProcessing(true);
    try {
      await api.post(`/payments/qris/static-confirm/${qrisPayment.id}`);
      toast.success("Pembayaran QRIS dikonfirmasi ✓");
      setPayOpen(false);
      const tx = qrisPayment.tx;
      setQrisPayment(null);
      clearCart();
      if (tx) {
        const paidTx = { ...tx, status: "paid", payment_method: "qris" };
        setReceiptTx(paidTx);
        if (currentOutlet?.printer_config?.auto_print_receipt) {
          setTimeout(() => printReceipt(paidTx, currentOutlet), 300);
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Konfirmasi pembayaran gagal");
    } finally {
      setProcessing(false);
    }
  }

  const currentOutlet = outlets.find(o => o.id === outletId);

  return (
    <div className="h-[calc(100vh-56px)] flex flex-col lg:flex-row bg-slate-100">
      {/* Products */}
      <div className="flex-1 flex flex-col min-w-0">
        <div className="p-4 bg-white border-b flex items-center gap-3 flex-wrap">
          <Select value={outletId} onValueChange={setOutletId}>
            <SelectTrigger data-testid="pos-outlet-select" className="w-56 h-10"><SelectValue placeholder="Pilih outlet"/></SelectTrigger>
            <SelectContent>
              {outlets.map(o => <SelectItem key={o.id} value={o.id}>{o.brand} · {o.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"/>
            <Input data-testid="pos-search-input" placeholder="Cari produk / SKU..." value={q} onChange={e=>setQ(e.target.value)} className="pl-9 h-10"/>
          </div>
          {currentOutlet && <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">{currentOutlet.brand}</Badge>}
          {user?.role === "kasir" && (activeShift ? <Badge className="bg-emerald-500">SHIFT OPEN</Badge> : <Button size="sm" variant="outline" onClick={() => { window.location.href = "/shifts"; }}>Buka Shift</Button>)}
        </div>
        <div className="flex-1 overflow-auto p-4">
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
            {filtered.map(p => {
              const effectiveStock = getEffectiveStock(p.id);
              const unavailable = effectiveStock !== null && effectiveStock !== undefined && effectiveStock <= 0;

              return (
              <button data-testid={`product-card-${p.id}`} key={p.id} onClick={() => addToCart(p)} disabled={unavailable}
                className={`group text-left bg-white rounded-xl border transition p-3 space-y-2 ${
                  unavailable
                    ? "opacity-50 cursor-not-allowed"
                    : "hover:border-orange-400 hover:shadow-lg"
                }`}>
                <div className="aspect-square rounded-lg bg-gradient-to-br from-orange-100 to-rose-100 flex items-center justify-center text-3xl font-bold text-orange-500 font-display">
                  {p.name[0]}
                </div>
                <div>
                  <div className="text-xs text-slate-500 truncate">{p.sku}</div>
                  <div className="text-sm font-semibold line-clamp-2 h-10">{p.name}</div>
                  <div className="flex items-center justify-between mt-1">
                    <div className="text-orange-600 font-extrabold num">{rupiah(p.price)}</div>
                    <Badge variant="outline" className={`text-[10px] ${
                      effectiveStock !== null && effectiveStock !== undefined && effectiveStock <= 0
                        ? "border-rose-300 text-rose-600 bg-rose-50"
                        : "border-slate-200 text-slate-500"
                    }`}>
                      {effectiveStock === null || effectiveStock === undefined ? "∞" : effectiveStock}
                    </Badge>
                  </div>
                </div>
              </button>
              );
            })}
            {!filtered.length && <div className="col-span-full text-center text-slate-400 py-16">Tidak ada produk</div>}
          </div>
        </div>
      </div>

      {/* Cart */}
      <div className="lg:w-96 bg-white border-l flex flex-col">
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <div>
            <div className="font-extrabold font-display text-lg">Keranjang</div>
            <div className="text-xs text-slate-500">{cart.length} item</div>
          </div>
          {cart.length > 0 && <Button data-testid="cart-clear-btn" variant="ghost" size="sm" onClick={clearCart} className="text-rose-600"><Trash2 className="w-4 h-4 mr-1"/>Kosongkan</Button>}
        </div>
        <div className="flex-1 overflow-auto">
          {cart.length === 0 ? (
            <div className="p-8 text-center text-slate-400 text-sm">Klik produk untuk menambah</div>
          ) : cart.map(c => (
            <div key={c.product_id} className="p-4 border-b flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{c.name}</div>
                <div className="text-xs text-slate-500 num">{rupiah(c.price)} × {c.qty}</div>
                <div className="flex items-center gap-2 mt-2">
                  <button data-testid={`cart-decrement-${c.product_id}`} onClick={() => updateQty(c.product_id, -1)} className="w-7 h-7 rounded-md bg-slate-100 hover:bg-slate-200 flex items-center justify-center"><Minus className="w-3 h-3"/></button>
                  <span className="text-sm font-semibold w-6 text-center num">{c.qty}</span>
                  <button data-testid={`cart-increment-${c.product_id}`} onClick={() => updateQty(c.product_id, 1)} className="w-7 h-7 rounded-md bg-slate-100 hover:bg-slate-200 flex items-center justify-center"><Plus className="w-3 h-3"/></button>
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm font-extrabold num">{rupiah(c.price * c.qty)}</div>
                <button onClick={() => removeItem(c.product_id)} className="mt-1 text-xs text-rose-500"><X className="w-4 h-4"/></button>
              </div>
            </div>
          ))}
        </div>
        <div className="p-5 border-t bg-slate-50 space-y-3">
          {/* Promo selector */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-xs text-slate-600 font-semibold"><Tag className="w-3.5 h-3.5"/>Promo</div>
            <div className="flex flex-wrap gap-2">
              <button data-testid="promo-none-btn" onClick={()=>setSelectedPromo(null)}
                className={`text-xs px-2 py-1 rounded-full border ${!selectedPromo ? "bg-slate-900 text-white border-slate-900":"bg-white border-slate-300"}`}>Tanpa Promo</button>
              {promos.map(p => (
                <button data-testid={`promo-btn-${p.id}`} key={p.id} onClick={()=>setSelectedPromo(p)}
                  className={`text-xs px-2 py-1 rounded-full border ${selectedPromo?.id===p.id ? "bg-orange-500 text-white border-orange-500":"bg-white border-orange-300 text-orange-600"}`}>
                  {p.name} · {p.type==="percent" ? `${p.value}%` : rupiah(p.value)}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <Input data-testid="promo-manual-input" type="number" placeholder="Diskon manual (Rp)" value={manualDisc} onChange={e=>setManualDisc(e.target.value)} className="h-8 text-xs"/>
              <Button data-testid="promo-manual-btn" size="sm" variant="outline" onClick={applyManual}>Terapkan</Button>
            </div>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-500">Subtotal</span><span className="font-medium num">{rupiah(subtotal)}</span>
          </div>
          {discount > 0 && (
            <div className="flex items-center justify-between text-sm text-emerald-600" data-testid="cart-discount">
              <span>Diskon {selectedPromo?.name ? `(${selectedPromo.name})` : ""}</span><span className="font-medium num">- {rupiah(discount)}</span>
            </div>
          )}
          <div className="flex items-center justify-between">
            <span className="text-slate-900 font-semibold">Total</span>
            <span className="text-2xl font-extrabold font-display num text-orange-600" data-testid="cart-total">{rupiah(total)}</span>
          </div>
          <div className="flex gap-2">
            <Button data-testid="pos-print-order-btn" onClick={() => {
              if (!cart.length) { toast.error("Cart kosong"); return; }
              const orderLike = {
                trx_no: `ORDER-${new Date().toISOString().slice(11,19).replace(/:/g,'')}`,
                created_at: new Date().toISOString(),
                cashier_name: user?.name || "-",
                items: cart.map(c => ({ ...c, subtotal: c.price * c.qty })),
                subtotal, total, discount, payment_method: "",
              };
              printOrder(orderLike, currentOutlet);
            }} variant="outline" className="flex-1 h-11" disabled={!cart.length}>
              <Printer className="w-4 h-4 mr-1"/>Print Order
            </Button>
            <Button data-testid="pos-pay-btn" onClick={() => { setPayOpen(true); setMethod("cash"); setCash(""); setQrisPayment(null); }}
              disabled={!cart.length || (user?.role === "kasir" && !activeShift)} className="flex-1 h-11 bg-orange-500 hover:bg-orange-600 text-white font-bold text-base">
              Bayar {rupiah(total)}
            </Button>
          </div>
        </div>
      </div>

      {/* Payment Dialog */}
      <Dialog open={payOpen} onOpenChange={(o)=>{setPayOpen(o); if(!o){setQrisPayment(null);}}}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display text-2xl">Pembayaran · {rupiah(total)}</DialogTitle></DialogHeader>

          {!qrisPayment && (
            <>
              <div className="space-y-1">
                <label className="text-sm font-medium text-slate-700">Pelanggan (opsional)</label>
                <Select value={customerId || "walkin"} onValueChange={v => setCustomerId(v === "walkin" ? "" : v)}>
                  <SelectTrigger><SelectValue placeholder="Pelanggan umum / pilih pelanggan" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="walkin">Pelanggan umum</SelectItem>
                    {customers.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}{c.phone ? ` · ${c.phone}` : ""}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {[
                  {v:"cash", l:"Cash", i:Banknote},
                  {v:"qris", l:"QRIS", i:QrCode},
                  {v:"debit", l:"Kartu", i:CreditCard},
                ].map(m => (
                  <button key={m.v} data-testid={`pay-method-${m.v}`} onClick={()=>setMethod(m.v)}
                    className={`p-4 rounded-xl border-2 flex flex-col items-center gap-2 transition ${method===m.v ? "border-orange-500 bg-orange-50":"border-slate-200 hover:border-slate-300"}`}>
                    <m.i className={`w-6 h-6 ${method===m.v ? "text-orange-600":"text-slate-600"}`}/>
                    <span className={`text-sm font-medium ${method===m.v ? "text-orange-700":"text-slate-700"}`}>{m.l}</span>
                  </button>
                ))}
              </div>

              {method === "cash" && (
                <div className="space-y-3">
                  <Input data-testid="pay-cash-input" type="number" placeholder="Nominal diterima" value={cash} onChange={e=>setCash(e.target.value)} className="h-12 text-lg num"/>
                  <div className="grid grid-cols-4 gap-2">
                    {[total, 50000, 100000, 200000].map((n,i) => (
                      <button key={i} onClick={()=>setCash(String(n))} className="p-2 rounded-md bg-slate-100 hover:bg-slate-200 text-xs num">{rupiah(n)}</button>
                    ))}
                  </div>
                  {cash && Number(cash) >= total && (
                    <div className="p-3 rounded-lg bg-emerald-50 text-emerald-700 text-sm">Kembalian: <b className="num">{rupiah(Number(cash) - total)}</b></div>
                  )}
                </div>
              )}
            </>
          )}

          {qrisPayment && (
            <div className="space-y-4 text-center py-2">
              <div className="mx-auto w-64 h-80 rounded-xl bg-white border-2 border-slate-200 p-2 flex items-center justify-center overflow-hidden">
                <img
                  src={qrisPayment.qr_image_url || "/qris-static.png"}
                  alt="QRIS Mazboy"
                  className="w-full h-full object-contain"
                />
              </div>
              <div>
                <div className="text-xs text-slate-500">Payment ID</div>
                <div className="font-mono text-xs">{qrisPayment.id}</div>
              </div>
              <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                Menunggu Pembayaran · QRIS STATIS
              </Badge>
              <div className="text-xs text-slate-500 leading-relaxed">
                Pastikan pembayaran sudah benar-benar masuk sebelum menekan tombol konfirmasi.
              </div>
              <Button data-testid="qris-static-confirm-btn" onClick={confirmStaticQrisPaid} disabled={processing} className="w-full bg-emerald-500 hover:bg-emerald-600">
                <Check className="w-4 h-4 mr-2"/> Pembayaran Sudah Masuk
              </Button>
            </div>
          )}

          {!qrisPayment && (
            <DialogFooter>
              <Button variant="outline" onClick={()=>setPayOpen(false)}>Batal</Button>
              <Button data-testid="pay-confirm-btn" onClick={submitPayment} disabled={processing} className="bg-orange-500 hover:bg-orange-600">
                {processing ? "Proses..." : method === "qris" ? "Generate QRIS" : "Konfirmasi"}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Receipt Modal */}
      <Receipt tx={receiptTx} outlet={currentOutlet} onClose={()=>setReceiptTx(null)} autoOpen={false}/>
    </div>
  );
}
